import { useAppTranslation } from '@/hooks/useAppTranslation';
import { FileUploaderField as FileUploaderFieldComponent } from '@/screens/ReportCreation/components/fields/FileUploaderField/FileUploaderField';
import type { ReportFormData } from '@/screens/ReportCreation/reportCreation.types';
import type { UploadedDocument } from '@/types/formField.types';
import { FieldTitle } from '@components/FieldTitle/FieldTitle';
import * as DocumentPicker from 'expo-document-picker';
import { useCallback, useMemo, useState } from 'react';
import {
  useController,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { YStack } from 'tamagui';
import type {
  ControlledFileUploaderFieldProps,
  DocumentSuccessResult,
} from './fileUploaderField.types';
import {
  buildAcceptedMimeTypes,
  isFileTypeAllowed,
  toUploadedDocument,
} from './fileUploaderField.utils';

export const ControlledFileUploaderField = <
  FormValues extends FieldValues = ReportFormData,
  FieldName extends FieldPath<FormValues> = FieldPath<FormValues>,
>({
  field,
  formController,
}: ControlledFileUploaderFieldProps<
  FormValues,
  FieldName
>): React.ReactElement => {
  const { t } = useAppTranslation();
  const { field: controllerField } = useController<FormValues, FieldName>({
    ...formController,
  });
  const [feedbackMessage, setFeedbackMessage] = useState<string | undefined>();
  const currentFiles = useMemo<UploadedDocument[]>(
    () => controllerField.value ?? [],
    [controllerField.value],
  );

  const acceptedMimeTypes = useMemo(
    () => buildAcceptedMimeTypes(field.acceptedFormats),
    [field.acceptedFormats],
  );

  const handleAddFiles = useCallback(async () => {
    setFeedbackMessage(undefined);

    try {
      const result = await DocumentPicker.getDocumentAsync({
        multiple: field.multiple,
        type: acceptedMimeTypes.length ? acceptedMimeTypes : ['*/*'],
        copyToCacheDirectory: true,
      });

      if (result.canceled) {
        return;
      }

      const successResult = result as DocumentSuccessResult;
      const assets = successResult.assets ?? [];

      if (!assets.length) {
        return;
      }

      const validatedFiles: UploadedDocument[] = [];
      let rejectedByType = false;
      let rejectedBySize = false;

      assets.forEach((asset) => {
        const uploadedDocument = toUploadedDocument(asset);

        if (
          !isFileTypeAllowed(
            uploadedDocument,
            field.acceptedFormats,
            acceptedMimeTypes,
          )
        ) {
          rejectedByType = true;
          return;
        }

        if (
          uploadedDocument.size &&
          uploadedDocument.size > field.maxFileSizeInBytes
        ) {
          rejectedBySize = true;
          return;
        }

        const alreadyAdded = currentFiles.some(
          (existing) =>
            existing.uri === uploadedDocument.uri &&
            existing.name === uploadedDocument.name,
        );

        if (!alreadyAdded) {
          validatedFiles.push(uploadedDocument);
        }
      });

      if (validatedFiles.length) {
        controllerField.onChange([...currentFiles, ...validatedFiles]);
      }

      if (rejectedByType) {
        setFeedbackMessage(t('reportCreation.step4.errors.unsupportedFormat'));
      } else if (rejectedBySize) {
        setFeedbackMessage(t('reportCreation.step4.errors.sizeExceeded'));
      }
    } catch (error) {
      console.error('File picker failed', error);
    }
  }, [
    acceptedMimeTypes,
    controllerField,
    currentFiles,
    field.acceptedFormats,
    field.maxFileSizeInBytes,
    field.multiple,
    t,
  ]);

  const handleRemoveFile = useCallback(
    (fileId: string) => {
      controllerField.onChange(
        currentFiles.filter((file) => file.id !== fileId),
      );
    },
    [controllerField, currentFiles],
  );

  return (
    <YStack gap="$2">
      <FieldTitle label={field.label} required={field.required} />

      <FileUploaderFieldComponent
        files={currentFiles}
        multiple={field.multiple}
        helperText={field.helperText}
        uploadPrompt={
          field.uploadPrompt ?? t('reportCreation.step4.helpers.tapToAddFile')
        }
        errorMessage={feedbackMessage}
        onUploadPress={handleAddFiles}
        onRemoveFile={handleRemoveFile}
      />
    </YStack>
  );
};
